import { handleErrors } from "common/reducers/Utils";
import { RootState, ThunkResult } from "common/reducers";
import { RootDispatchType, RootGetState } from "common/store";
import {
  create,
  destroy,
  exportEstimateTemplate,
  IImportEstimateTemplateResponse,
  importEstimateTemplate,
  list,
  update,
} from "common/api/estimateTemplate.service";
import { createDirect as createDirectImage, directUpload as imageDirectUpload } from "common/api/image.service";
import { create as createDocument, directUpload as documentDirectUpload } from "common/api/document.service";
import { imageFromJSON } from "common/records/Image";
import { documentFromJSON } from "common/records/Document";
import { DANGER, SUCCESS, WARNING, addFlashAlert } from "common/components/FlashAlert";
import { EstimateTemplateParamsRecord } from "common/records/index/EstimateTemplateParams";
import {
  EstimateTemplateCustomProductRecord,
  EstimateTemplateGroupContentRecord,
  EstimateTemplateRecord,
  IEstimateTemplateContent,
  IEstimateTemplateCustomProduct,
  IEstimateTemplateData,
  estimateTemplateFromJSON,
} from "common/records/EstimateTemplate";
import { estimateTemplate as estimateTemplateSelector } from "common/selectors/estimateTemplate.selectors";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { dirtyIds, ids } from "common/selectors/idsManagement.selectors";
import { EstimateTemplateActions } from "common/reducers/estimateTemplate.actions";
import {
  ApplyTemplateMode,
  EstimateEditorActions,
  IEstimateTemplateToApply,
  ITemplateGroupData,
  ITemplateLineItemData,
} from "common/reducers/estimateEditor.actions";
import { EstimateLineItemAsyncActions } from "common/reducers/estimateLineItem.async.actions";
import { EstimateEditorAsyncActions } from "common/reducers/estimateEditor.async.actions";
import { currentOrgId } from "common/selectors/org.selectors";
import { currentEstimate, currentEstimateActivatedPriceListId } from "common/selectors/estimate.selectors";
import { currentEstimateGroups, estimateGroupLineItemIds } from "common/selectors/estimateGroup.selectors";
import { estimateLineItem, hasFileStagingForLineItem } from "common/selectors/estimateLineItem.selectors";
import { estimateLineItemOptions } from "common/selectors/estimateLineItemOption.selectors";
import { image as imageSelector } from "common/selectors/image.selectors";
import { document as documentSelector } from "common/selectors/document.selectors";
import { productByUuid } from "common/selectors/product.selectors";
import { productOptionsByUuid } from "common/selectors/productOption.selectors";
import { loadEstimateActivatedPriceList } from "src/router/loaders/jobs/Estimates/activatedPriceList";
import { trackEventHelper } from "common/helpers/Analytics";
import { resolveFiles } from "common/helpers/resolveFiles";
import { List } from "immutable";
import { exportFileToS3 } from "common/helpers/File";

export const ESTIMATE_TEMPLATES_MODEL = "estimateTemplates";
/**
 * Selection model for the Manage modal's move-to-folder, kept separate from the displayed-list model
 * (`ESTIMATE_TEMPLATES_MODEL`) so checking a row marks it for move without disturbing the list — the
 * Photos/Documents pattern (`jobImages` / `jobDocuments`).
 */
export const SELECTED_ESTIMATE_TEMPLATES_MODEL = "selectedEstimateTemplates";

interface ICustomFileSource {
  groupIndex: number;
  uuid: string;
  imageIds: List<number>;
  documentIds: List<number>;
}

/**
 * Builds the template `content` payload from the current estimate: groups (name/included/sort_order),
 * product line items keyed by product uuid → selected option uuids, and custom line items, preserving
 * the interleaved order via `line_item_order`. Returns the content plus, for each custom item that
 * carries files, the source estimate-line-item image/document ids so the save path can upload them to
 * the template (see `attachCustomItemFiles`). Reads uuids/file-ids straight off the records — no APL
 * snapshot resolution needed (occ-app line items already carry `product_uuid` / `product_option_uuid`).
 */
const buildTemplateContent = (
  state: RootState,
): { content: IEstimateTemplateContent; fileSources: ICustomFileSource[] } => {
  const fileSources: ICustomFileSource[] = [];

  const groups = currentEstimateGroups(state)
    .toArray()
    .map((group, groupIndex) => {
      const itemUuids: { [productUuid: string]: string[] } = {};
      const customProducts: IEstimateTemplateCustomProduct[] = [];
      const lineItemOrder: { type: "item" | "custom"; order: string }[] = [];

      estimateGroupLineItemIds(state, { estimateGroupId: group.id }).forEach((lineItemId) => {
        const lineItem = estimateLineItem(state, { estimateLineItemId: lineItemId });
        if (!lineItem || lineItem._destroy) return;

        if (lineItem.product_id == null) {
          const uuid = lineItem.uuid;
          customProducts.push({
            uuid,
            name: lineItem.name,
            description: lineItem.description,
            price: lineItem.base_product_price,
            quantity: lineItem.quantity,
            uom: lineItem.uom,
            image_ids: [],
            document_ids: [],
          });
          lineItemOrder.push({ type: "custom", order: uuid });
          // A soft-deleted (`_destroy`) file that hasn't been flushed to the server still carries its
          // id on the line item; excluding it here keeps a deleted attachment from being re-uploaded
          // into the template and resurrected on apply.
          const imageIds = lineItem.image_ids.filter((id) => {
            const record = imageSelector(state, { imageId: id });
            return record != null && !record._destroy;
          });
          const documentIds = lineItem.document_ids.filter((id) => {
            const record = documentSelector(state, { documentId: id });
            return record != null && !record._destroy;
          });
          if (!imageIds.isEmpty() || !documentIds.isEmpty()) {
            fileSources.push({ groupIndex, uuid, imageIds, documentIds });
          }
        } else {
          const productUuid = lineItem.product_uuid;
          itemUuids[productUuid] = estimateLineItemOptions(state, {
            estimateLineItemOptionIds: lineItem.option_ids,
            fullEstimate: true,
          })
            .filter((option) => option && !option._destroy)
            .map((option) => option.product_option_uuid)
            .filter((uuid): uuid is string => Boolean(uuid))
            .toArray();
          lineItemOrder.push({ type: "item", order: productUuid });
        }
      });

      return {
        name: group.name,
        included: group.included,
        sort_order: group.sort_order ?? 0,
        item_uuids: itemUuids,
        custom_products: customProducts,
        line_item_order: lineItemOrder,
      };
    });

  return { content: { groups }, fileSources };
};

/** Uploads files to a template via the S3 direct-upload path and returns the new template file ids. */
const uploadFilesToTemplate = async (
  templateId: number,
  files: File[],
  kind: "image" | "document",
): Promise<number[]> => {
  if (!files.length) return [];
  const { urls } =
    kind === "image"
      ? await imageDirectUpload(files.length, "EstimateTemplate", templateId)
      : await documentDirectUpload(files.length, "EstimateTemplate", templateId);
  // The server must presign one slot per file. Fewer slots is an abnormal server-contract condition:
  // silently skipping the un-presigned files would drop attachments while still reporting the save as
  // clean. Throw instead, so the enclosing `attachFilesOrWarn` surfaces the soft "some files could not
  // be attached" warning rather than losing files without a trace.
  if (urls.length < files.length) {
    throw new Error(`Expected ${files.length} presigned ${kind} upload slots but received ${urls.length}.`);
  }
  const ids: number[] = [];
  await Promise.all(
    files.map(async (file, index) => {
      const obj = urls[index];
      if (!obj) return;
      await exportFileToS3(obj.url, file);
      if (kind === "image") {
        const response = await createDirectImage(
          imageFromJSON({
            uuid: obj.id,
            name: file.name,
            imageable_id: templateId,
            imageable_type: "EstimateTemplate",
          }),
        );
        ids[index] = response.image.id;
      } else {
        const response = await createDocument(
          documentFromJSON({
            uuid: obj.id,
            name: file.name,
            documentable_id: templateId,
            documentable_type: "EstimateTemplate",
          }),
        );
        ids[index] = response.document.id;
      }
    }),
  );
  return ids.filter((id) => id != null);
};

/**
 * After a template is created/overwritten, uploads each custom item's files to the template (S3
 * direct-upload) and, when any landed, re-PATCHes the content so the stored `custom_products` carry
 * the template's file ids — the link the apply path reads to restore files. Receives the final
 * template into the store either way.
 */
const attachCustomItemFiles = async (
  dispatch: RootDispatchType,
  getState: RootGetState,
  orgId: number,
  estimateTemplate: IEstimateTemplateData,
  content: IEstimateTemplateContent,
  fileSources: ICustomFileSource[],
): Promise<void> => {
  let changed = false;
  for (const source of fileSources) {
    const state = getState();
    const imageFiles = (await resolveFiles(state, source.imageIds, "image")).map((r) => r.file);
    const documentFiles = (await resolveFiles(state, source.documentIds, "document")).map((r) => r.file);
    const newImageIds = await uploadFilesToTemplate(estimateTemplate.id, imageFiles, "image");
    const newDocumentIds = await uploadFilesToTemplate(estimateTemplate.id, documentFiles, "document");
    if (newImageIds.length || newDocumentIds.length) {
      const customProduct = content.groups[source.groupIndex]?.custom_products?.find((cp) => cp.uuid === source.uuid);
      if (customProduct) {
        customProduct.image_ids = newImageIds;
        customProduct.document_ids = newDocumentIds;
        changed = true;
      }
    }
  }

  if (changed) {
    const updatedRecord = estimateTemplateFromJSON({ ...estimateTemplate, template: content });
    const response = await update(orgId, updatedRecord, { includeTemplate: true });
    dispatch(EstimateTemplateActions.receiveEstimateTemplate(response.estimate_template));
  } else {
    dispatch(EstimateTemplateActions.receiveEstimateTemplate(estimateTemplate));
  }
};

/**
 * Runs the custom-item file attach but never lets its failure roll back the already-succeeded
 * create/overwrite: the template is persisted by the time this runs, so a file failure is a soft
 * warning, not a "save failed" (which would invite a duplicate-creating retry).
 */
const attachFilesOrWarn = async (
  dispatch: RootDispatchType,
  getState: RootGetState,
  orgId: number,
  estimateTemplate: IEstimateTemplateData,
  content: IEstimateTemplateContent,
  fileSources: ICustomFileSource[],
): Promise<void> => {
  try {
    await attachCustomItemFiles(dispatch, getState, orgId, estimateTemplate, content, fileSources);
  } catch (error) {
    handleErrors(error);
    addFlashAlert(WARNING, "The template was saved, but some files could not be attached.");
  }
};

/**
 * The id of the freshly-applied custom line item carrying `customUuid` (stored as `product_uuid`).
 * Prefers a match that doesn't already have files staged so re-applying the same template (merge mode,
 * where an earlier apply left a line item with the same `product_uuid`) restores onto the new row, not
 * the prior one; falls back to the first match.
 */
export const findAppliedCustomLineItemId = (state: RootState, customUuid: string): number | undefined => {
  let firstMatch: number | undefined;
  let unstagedMatch: number | undefined;
  currentEstimateGroups(state).forEach((group) => {
    estimateGroupLineItemIds(state, { estimateGroupId: group.id }).forEach((id) => {
      const lineItem = estimateLineItem(state, { estimateLineItemId: id });
      if (!lineItem || lineItem.product_id != null || lineItem.product_uuid !== customUuid) return;
      if (firstMatch === undefined) firstMatch = lineItem.id;
      if (unstagedMatch === undefined && !hasFileStagingForLineItem(state, { estimateLineItemId: lineItem.id })) {
        unstagedMatch = lineItem.id;
      }
    });
  });
  return unstagedMatch ?? firstMatch;
};

/**
 * After a template is applied, restores each custom item's attached files: downloads the template's
 * stored files and stages them onto the matching new custom line item (matched by `product_uuid`),
 * reusing the line-item file-staging path so the estimate's save flush uploads them. No-op when no
 * custom item carries files — the common path, so applies without files are unchanged.
 */
export const restoreCustomItemFiles = async (
  dispatch: RootDispatchType,
  getState: RootGetState,
  templateRecord: EstimateTemplateRecord,
): Promise<void> => {
  const customsWithFiles: EstimateTemplateCustomProductRecord[] = [];
  (templateRecord.template?.groups ?? List<EstimateTemplateGroupContentRecord>()).forEach((group) => {
    group.custom_products.forEach((customProduct) => {
      if (customProduct.uuid && (!customProduct.image_ids.isEmpty() || !customProduct.document_ids.isEmpty())) {
        customsWithFiles.push(customProduct);
      }
    });
  });
  if (!customsWithFiles.length) return;

  for (const customProduct of customsWithFiles) {
    if (!customProduct.uuid) continue;
    const state = getState();
    const lineItemId = findAppliedCustomLineItemId(state, customProduct.uuid);
    if (!lineItemId) continue;
    const files = [
      ...(await resolveFiles(state, customProduct.image_ids, "image")).map((r) => r.file),
      ...(await resolveFiles(state, customProduct.document_ids, "document")).map((r) => r.file),
    ];
    files.forEach((file) => dispatch(EstimateLineItemAsyncActions.addFileToLineItem(lineItemId, file)));
  }
};

/**
 * Runs the apply-side file restore but never lets its failure roll back the already-succeeded apply:
 * by the time this runs the template has been applied to the editor draft and the analytics have
 * fired, so a file failure is a soft warning — not an apply failure — and must not skip `onApplied()`
 * (navigate back) or surface an unhandled rejection. Symmetric to the save path's `attachFilesOrWarn`.
 */
const restoreFilesOrWarn = async (
  dispatch: RootDispatchType,
  getState: RootGetState,
  templateRecord: EstimateTemplateRecord,
): Promise<void> => {
  try {
    await restoreCustomItemFiles(dispatch, getState, templateRecord);
  } catch (error) {
    handleErrors(error);
    addFlashAlert(WARNING, "The template was applied, but some files could not be restored.");
  }
};

export const EstimateTemplateAsyncActions = {
  importEstimateTemplate: (
    orgId: number,
    document: File,
  ): ThunkResult<Promise<IImportEstimateTemplateResponse | undefined>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        const response = await importEstimateTemplate(orgId, document);
        if (response.message) {
          addFlashAlert("success", response.message);
        }
        return response;
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  exportEstimateTemplate: (orgId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        await exportEstimateTemplate(orgId);
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  /**
   * Light list of the org's standard estimate templates for the picker (IdsManagement model
   * "estimateTemplates"). Mirrors DocumentAsyncActions.listDocuments.
   */
  listEstimateTemplates: (queryParams: EstimateTemplateParamsRecord): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        dispatch(IdsManagementActions.receiveModel(ESTIMATE_TEMPLATES_MODEL, List()));
        return;
      }

      dispatch(IdsManagementActions.fetchModel(ESTIMATE_TEMPLATES_MODEL, queryParams));

      try {
        const response = await list(orgId, queryParams, { useGlobalLoader: false });
        dispatch(EstimateTemplateActions.receiveEstimateTemplates(response.estimate_templates));

        const ids = List(
          response.estimate_templates.map((estimateTemplate: IEstimateTemplateData) => estimateTemplate.id),
        );
        dispatch(IdsManagementActions.receiveModel(ESTIMATE_TEMPLATES_MODEL, ids, response?.meta?.pagination));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(ESTIMATE_TEMPLATES_MODEL, List()));
      }
    };
  },
  /**
   * Reads the full template off the list-hydrated Record (the index already returns the heavy
   * `template` payload — no `show` fetch), resolves its product/option UUIDs to ids against the
   * estimate's hydrated activated price list, and applies it to the editor draft. Ports rsf-app
   * `EstimatorService.setupTemplate` + `EstimateTemplate.addTemplate`: resolution happens here; the
   * reducer derives line-item fields from the resolved product. When a group carries `line_item_order`
   * the line items are built in that authored order (products and custom items interleaved); otherwise
   * the products-then-customs fallback applies. Partial-apply — unresolved products are skipped and
   * counted toward the "X out of possible Y" warning; custom products always apply. When the template
   * isn't in the store, or when nothing at all resolves (total resolution failure), the picker stays
   * open (no apply, no close).
   *
   * @param estimateId The estimate to apply onto
   * @param templateId The template to apply (looked up from the store)
   * @param mode merge or replace (resolved by the picker from `add_products` + confirm dialogs)
   */
  applyEstimateTemplate: (
    estimateId: number,
    templateId: number,
    mode: ApplyTemplateMode,
  ): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert(DANGER, "Could not determine the current organization.");
        return false;
      }

      const templateRecord = estimateTemplateSelector(getState(), { id: templateId });
      if (!templateRecord) {
        addFlashAlert(DANGER, "This template could not be loaded.");
        return false;
      }

      // Defensive: the route loader already hydrated the estimate's APL before the picker mounted;
      // this is a no-op when hydrated and never re-implements the fetch.
      await loadEstimateActivatedPriceList(estimateId);

      const state = getState();
      const activatedPriceListId = currentEstimateActivatedPriceListId(state);

      let resolvedCount = 0;
      let possibleCount = 0;
      // Track product resolution separately from the overall counts: custom products always resolve,
      // so they must not mask a total *product* resolution failure in the replace-abort guard below.
      let productPossibleCount = 0;
      let productResolvedCount = 0;

      const templateGroups = templateRecord.template?.groups ?? List<EstimateTemplateGroupContentRecord>();
      const groups: ITemplateGroupData[] = templateGroups.toArray().map((group) => {
        const lineItems: ITemplateLineItemData[] = [];

        const itemUuids = group.item_uuids;
        const customProducts = group.custom_products;
        const lineItemOrder = group.line_item_order;

        // Resolves one product uuid against the APL and pushes the resulting line item (skipping a
        // product the price list doesn't contain). Shared by the ordered and fallback paths.
        const addProduct = (productUuid: string) => {
          possibleCount += 1;
          productPossibleCount += 1;
          const product = productByUuid(state, { productUuid, activatedPriceListId });
          if (!product) return; // not in this price list — skip (no placeholder row)
          const optionUuids = (itemUuids.get(productUuid) ?? List<string>()).toArray();
          const options = productOptionsByUuid(state, { product, optionUuids });
          lineItems.push({ productId: product.id, productUuid, options });
          resolvedCount += 1;
          productResolvedCount += 1;
        };

        const addCustom = (custom: EstimateTemplateCustomProductRecord) => {
          possibleCount += 1;
          resolvedCount += 1; // custom products are added unconditionally
          const uuid = custom.uuid ?? undefined;
          lineItems.push({
            options: [],
            productUuid: uuid,
            custom: {
              name: custom.name ?? "",
              description: custom.description ?? "",
              productPrice: custom.price ?? 0,
              laborPrice: 0,
              uom: custom.uom ?? "",
              quantity: custom.quantity ?? 1,
              uuid,
            },
          });
        };

        if (!lineItemOrder.isEmpty()) {
          // Authored order (rsf-app setupTemplate line_item_order replay): interleave products and
          // custom items exactly as the template author arranged them.
          lineItemOrder.forEach((entry) => {
            const orderUuid = entry.order;
            if (orderUuid == null) return;
            if (entry.type === "custom") {
              const custom = customProducts.find((cp) => cp.uuid === orderUuid);
              if (custom) addCustom(custom);
            } else {
              addProduct(orderUuid);
            }
          });
        } else {
          // Fallback (no authored order): all products first, then all custom items.
          itemUuids.keySeq().forEach((productUuid) => addProduct(productUuid));
          customProducts.forEach((custom) => addCustom(custom));
        }

        return {
          name: group.name,
          included: group.included,
          sortOrder: group.sort_order,
          lineItems,
        };
      });

      // Total product-resolution failure is a different class from partial-apply: when the template
      // has products but none resolve, the price-list snapshot is missing/failed and a replace would
      // wipe the estimate for nothing. Abort (error toast, keep picker open). Guard on *product*
      // counts, not totals: a template with custom products would
      // otherwise survive a total product failure (customs always resolve) and replace-mode would
      // still clear every group. A custom-products-only template (no products) still applies.
      if (productPossibleCount > 0 && productResolvedCount === 0) {
        addFlashAlert(DANGER, "None of this template's products could be added to this estimate.");
        return false;
      }

      const template: IEstimateTemplateToApply = {
        id: templateRecord.id,
        name: templateRecord.name ?? "",
        groups,
      };

      // Snapshot the estimate's group ids before applying so we can fire "estimate group created"
      // for genuinely new groups only. The thunk can't see the reducer's per-group merge-vs-create
      // outcome, so diffing the group ids after the dispatch is the reliable signal — correct for
      // both merge (some groups match existing) and replace (every group is new).
      const groupIdsBefore = new Set<number>((currentEstimate(state)?.group_ids ?? List<number>()).toArray());

      dispatch(EstimateEditorActions.applyEstimateTemplate(estimateId, template, mode));
      // Recompute totals AND re-run validations via the chokepoint — applying a template adds
      // line items, so the validation gap (template-apply previously did a bare recalc and never
      // validated the added items) is closed here. Unlike the save path, the apply path
      // intentionally leaves the estimate dirty, so no commitGroupTotalsToLastSaved follows.
      dispatch(EstimateEditorAsyncActions.afterEstimateMutation(estimateId));

      // Apply-funnel analytics (rsf-app EstimateTemplate.addTemplate parity). Tracking lives in the
      // thunk, never the reducer (reducers stay pure). Event names port verbatim; payload keys follow
      // occ-app's TrackingData convention.
      currentEstimateGroups(getState()).forEach((group) => {
        if (!groupIdsBefore.has(group.id)) {
          trackEventHelper({
            action: "estimate group created",
            category: "Estimate",
            estimateGroupName: group.name,
            estimateTemplate: template.id,
          });
        }
      });
      groups.forEach((group) => {
        group.lineItems.forEach((lineItem) => {
          trackEventHelper({
            action: "product added",
            category: "Estimate",
            productId: lineItem.productId,
            estimateGroupName: group.name,
            estimateTemplate: template.id,
          });
          lineItem.options.forEach((option) => {
            trackEventHelper({
              action: "product option added",
              category: "Estimate",
              productOptionId: option.productOptionId,
              productId: lineItem.productId,
              estimateTemplate: template.id,
            });
          });
        });
      });

      // Restore any files the template's custom items carry onto the newly-applied line items. Wrapped
      // so a file-restore failure can't skip the successful-apply outcome (`onApplied()`) or escape as
      // an unhandled rejection — the estimate is already mutated, so file failure is a soft warning.
      await restoreFilesOrWarn(dispatch, getState, templateRecord);

      if (resolvedCount >= possibleCount) {
        addFlashAlert(SUCCESS, "Template added to your estimate.");
      } else {
        addFlashAlert(WARNING, `${resolvedCount} out of possible ${possibleCount} products were added.`);
      }
      return true;
    };
  },

  /**
   * Captures the current estimate as a new template in `folderId`, then uploads its custom-line-item
   * files to the template (S3 direct-upload). Returns the new template id, or undefined on failure.
   */
  createEstimateTemplate: (name: string, folderId: number | null): ThunkResult<Promise<number | undefined>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();
      const orgId = currentOrgId(state);
      if (!orgId) {
        addFlashAlert(DANGER, "Could not determine the current organization.");
        return undefined;
      }

      const { content, fileSources } = buildTemplateContent(state);
      const record = estimateTemplateFromJSON({
        name,
        folder_id: folderId ?? null,
        kind: "standard",
        template: content,
      });

      try {
        const { estimate_template } = await create(orgId, record);
        // The template now exists server-side — record it and report success before the (separable)
        // file attach, so a file-upload failure doesn't masquerade as a failed create and prompt a
        // retry that creates a duplicate template.
        dispatch(EstimateTemplateActions.receiveEstimateTemplate(estimate_template));
        addFlashAlert(SUCCESS, "Saved estimate template successfully");
        await attachFilesOrWarn(dispatch, getState, orgId, estimate_template, content, fileSources);
        return estimate_template.id;
      } catch (response) {
        handleErrors(response);
        addFlashAlert(WARNING, "Unable to save estimate template. If the problem persists, contact support.");
        return undefined;
      }
    };
  },

  /**
   * Overwrites an existing template's content with the current estimate's structure (clearing the
   * template's prior files via `delete_files`), then re-uploads the current custom-item files.
   */
  overwriteEstimateTemplate: (templateId: number): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();
      const orgId = currentOrgId(state);
      const existing = estimateTemplateSelector(state, { id: templateId });
      if (!orgId || !existing) {
        addFlashAlert(DANGER, "This template could not be loaded.");
        return false;
      }

      const { content, fileSources } = buildTemplateContent(state);
      const record = estimateTemplateFromJSON({
        id: templateId,
        name: existing.name ?? "",
        folder_id: existing.folder_id,
        kind: existing.kind ?? "standard",
        template: content,
      });

      try {
        const { estimate_template } = await update(orgId, record, { includeTemplate: true, deleteFiles: true });
        dispatch(EstimateTemplateActions.receiveEstimateTemplate(estimate_template));
        addFlashAlert(SUCCESS, "Saved estimate template successfully");
        await attachFilesOrWarn(dispatch, getState, orgId, estimate_template, content, fileSources);
        return true;
      } catch (response) {
        handleErrors(response);
        addFlashAlert(WARNING, "Unable to save estimate template. If the problem persists, contact support.");
        return false;
      }
    };
  },

  /** Persists a metadata-only change (rename / folder move / reorder) for one template. */
  updateEstimateTemplate: (templateId: number, isSingleUpdate = true): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();
      const orgId = currentOrgId(state);
      const record = estimateTemplateSelector(state, { id: templateId });
      if (!orgId || !record) return;

      try {
        const { estimate_template } = await update(orgId, record, { includeTemplate: false });
        dispatch(EstimateTemplateActions.receiveEstimateTemplate(estimate_template));
        if (isSingleUpdate) addFlashAlert(SUCCESS, "Saved estimate template successfully");
      } catch (response) {
        handleErrors(response);
      }
    };
  },

  /** Inline-rename: optimistically edits the name, then persists it. */
  renameEstimateTemplate: (templateId: number, name: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(EstimateTemplateActions.editEstimateTemplate(templateId, "name", name));
      await dispatch(EstimateTemplateAsyncActions.updateEstimateTemplate(templateId));
    };
  },

  /** Deletes a template and drops it from the listed ids. */
  destroyEstimateTemplate: (templateId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) return;

      dispatch(EstimateTemplateActions.fetchEstimateTemplate(templateId));
      try {
        await destroy(orgId, templateId);
        dispatch(EstimateTemplateActions.removeEstimateTemplate(templateId));
        dispatch(IdsManagementActions.removeId(templateId, ESTIMATE_TEMPLATES_MODEL));
        addFlashAlert(SUCCESS, "Estimate template successfully deleted.");
      } catch (response) {
        handleErrors(response);
        dispatch(EstimateTemplateActions.setEstimateTemplateLoaded(templateId));
      }
    };
  },

  /**
   * Persists the same field across many templates (batch folder move / reorder). Fires a single
   * whole-batch success toast on success; on failure surfaces a danger toast, marks the rows loaded,
   * and — when `reject` is set — rejects so the caller (move) can roll its optimistic edit back. Ports
   * rsf-app `batchUpdateEstimateTemplate(ids, reject)`: the PATCH errors must surface (a swallowed error
   * that still fired "Saved successfully" is the bug this replaces), so the updates are awaited directly
   * here rather than delegated to the per-row `updateEstimateTemplate` (which swallows via handleErrors).
   */
  batchUpdateEstimateTemplate: (ids: List<number>, reject = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());
      if (!orgId) return;

      ids.forEach((id) => dispatch(EstimateTemplateActions.fetchEstimateTemplate(id)));

      try {
        const responses = await Promise.all(
          ids
            .toArray()
            .map((id) => estimateTemplateSelector(getState(), { id }))
            .filter((record): record is EstimateTemplateRecord => Boolean(record))
            .map((record) => update(orgId, record, { includeTemplate: false })),
        );
        dispatch(
          EstimateTemplateActions.receiveEstimateTemplates(responses.map((response) => response.estimate_template)),
        );
        addFlashAlert(SUCCESS, "Saved estimate templates successfully");
      } catch (response) {
        ids.forEach((id) => dispatch(EstimateTemplateActions.setEstimateTemplateLoaded(id)));
        addFlashAlert(DANGER, "Update failed, please try again.");
        if (reject) return Promise.reject();
      }
    };
  },

  /** Drag-reorder: reorders the listed ids (updating sort_order) and persists the changed rows. */
  onSortEnd: (oldIndex: number, newIndex: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(IdsManagementActions.onSortEnd(ESTIMATE_TEMPLATES_MODEL, oldIndex, newIndex));

      const ids = dirtyIds(getState()) as List<number>;
      if (!ids || ids.size === 0) return;

      await dispatch(EstimateTemplateAsyncActions.batchUpdateEstimateTemplate(ids));
      dispatch(IdsManagementActions.setDirtyIds(null));
    };
  },

  /**
   * Moves the *selected* templates into `folderId` (mirrors DocumentAsyncActions.moveDocuments): reads
   * the selection model (default `SELECTED_ESTIMATE_TEMPLATES_MODEL`), edits each row's folder_id,
   * persists them, drops them from the displayed-list view, then clears the selection. No-op when
   * nothing is selected — so it can never move the whole folder by accident.
   */
  moveEstimateTemplates: (
    folderId: number,
    modelName: string = SELECTED_ESTIMATE_TEMPLATES_MODEL,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const selectedIds = ids(getState(), { modelName }) as List<number>;
      if (!selectedIds || selectedIds.size === 0) return;

      // Pre-move folder for rollback. The displayed rows all live in the browsed folder, so reverting
      // the whole selection to the first row's folder restores the pre-move state (rsf-app parity).
      const originalFolderId = estimateTemplateSelector(getState(), { id: selectedIds.first() as number })?.folder_id;

      dispatch(IdsManagementActions.fetchModel(modelName));
      dispatch(EstimateTemplateActions.editEstimateTemplates(selectedIds, "folder_id", folderId));
      try {
        // `reject` so a failed PATCH throws here instead of silently reporting success.
        await dispatch(EstimateTemplateAsyncActions.batchUpdateEstimateTemplate(selectedIds, true));
        selectedIds.forEach((id) => dispatch(IdsManagementActions.removeId(id, ESTIMATE_TEMPLATES_MODEL)));
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      } catch {
        // Persist failed: roll the optimistic folder_id back and keep the rows in the view (no
        // removeId) so the UI matches the unchanged server state. Selection is preserved for retry.
        dispatch(EstimateTemplateActions.editEstimateTemplates(selectedIds, "folder_id", originalFolderId ?? null));
        dispatch(IdsManagementActions.receiveModel(modelName, selectedIds));
      }
    };
  },
};
